/**
 * Customer Search Modal
 * Search customers by name or CPF for fiado payments.
 * Shows customer info: name, credit limit, outstanding balance, available credit.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, User, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';

interface Customer {
  id: string;
  full_name: string;
  cpf: string;
  phone: string;
  credit_limit: number | null;
  outstanding_balance: number;
  available_credit: number | null;
}

interface CustomerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

export function CustomerSearchModal({ isOpen, onClose, onSelect }: CustomerSearchModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setResults([]);
      setHasSearched(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) return;

    setIsLoading(true);
    setHasSearched(true);

    try {
      const response = await apiFetch(`/api/customers?search=${encodeURIComponent(searchTerm)}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        const customers: Customer[] = data.data.map((c: Record<string, unknown>) => {
          const creditLimit = c.credit_limit === null ? null : Number(c.credit_limit);
          const outstandingBalance = Number(c.outstanding_balance);
          const availableCredit = creditLimit === null
            ? null
            : Math.max(0, creditLimit - outstandingBalance);

          return {
            id: c.id as string,
            full_name: c.full_name as string,
            cpf: c.cpf as string,
            phone: c.phone as string,
            credit_limit: creditLimit,
            outstanding_balance: outstandingBalance,
            available_credit: availableCredit,
          };
        });
        setResults(customers);
      }
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md rounded-2xl border border-border/50 bg-card p-6 shadow-2xl"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>

          <h2 className="mb-4 text-lg font-bold text-foreground">Buscar Cliente</h2>

          {/* Search input */}
          <div className="relative mb-4">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="Nome ou CPF do cliente..."
              className="w-full rounded-xl border border-border bg-muted/30 py-3 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <button
            onClick={handleSearch}
            disabled={isLoading || !searchTerm.trim()}
            className="mb-4 w-full rounded-xl bg-primary/10 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/20 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Buscar'}
          </button>

          {/* Results */}
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {results.map((customer) => (
              <button
                key={customer.id}
                onClick={() => onSelect(customer)}
                className="w-full rounded-xl border border-border/50 bg-muted/10 p-3 text-left transition-all hover:border-primary/50 hover:bg-muted/30"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{customer.full_name}</p>
                    <p className="text-xs text-muted-foreground">CPF: {customer.cpf}</p>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Limite</span>
                    <p className="font-medium text-foreground">
                      {customer.credit_limit === null ? '∞' : formatCurrency(customer.credit_limit)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Devendo</span>
                    <p className="font-medium text-red-400">
                      {formatCurrency(customer.outstanding_balance)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Disponível</span>
                    <p className="font-medium text-green-400">
                      {customer.available_credit === null ? '∞' : formatCurrency(customer.available_credit)}
                    </p>
                  </div>
                </div>
              </button>
            ))}

            {hasSearched && results.length === 0 && !isLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Nenhum cliente encontrado
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
